/**
 * Провайдер режима рабочего пространства (E4.5). Режим определяет адрес
 * (training_instance_id) или активный экземпляр вкладки. Источник данных переключается до
 * монтирования страниц: при каждой смене контура страницы размонтируются, маршрутизатор
 * HTTP-клиента меняется, запросы прежнего контура отменяются, и только затем страницы
 * монтируются заново со своим кешем. Учебный экземпляр открывается после загрузки его
 * manifest владельцем; чужой или удалённый -- ошибка без создания нового примера, сбой загрузки
 * manifest -- повтор без забывания экземпляра.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { ApiError, setRequestRouter } from '@/api/client';
import { getTrainingInstance, resetTrainingInstance, type TrainingManifest } from '@/api/training';
import { WorkspaceIcon } from '@/components/workspace/WorkspaceIcon';
import { WorkspaceState } from '@/components/workspace/WorkspaceState';
import {
	clearActiveInstance,
	readActiveInstance,
	writeActiveInstance,
} from '@/features/training/activeInstance';
import { trainingDataSource } from '@/features/training/trainingDataSource';
import { leaveTour } from '@/features/training/tourStore';
import { sessionIdentity, useSessionStore } from '@/stores/session';
import { liveDataSource, type WorkspaceDataSource } from './liveDataSource';
import {
	WorkspaceDataSourceContext,
	type WorkspaceDataSourceValue,
} from './WorkspaceDataSourceContext';
import {
	TRAINING_INSTANCE_PARAM,
	trainingInstanceFromRoute,
	workspaceHref,
	withoutTrainingParam,
} from './workspaceRoutes';

/** Состояние перехода: что сейчас подключено к HTTP-клиенту */
type Active =
	| { kind: 'switching' }
	| {
			kind: 'live';
			identity: string | null;
			sessionGeneration: number;
			source: WorkspaceDataSource;
	  }
	| {
			kind: 'training';
			identity: string | null;
			sessionGeneration: number;
			instanceId: string;
			generation: number;
			manifest: TrainingManifest;
			source: WorkspaceDataSource;
	  }
	| { kind: 'unavailable'; identity: string | null; sessionGeneration: number; instanceId: string }
	/* manifest не загрузился (сбой сети, 5xx): экземпляр не забывается, загрузка повторяется */
	| { kind: 'failed'; identity: string | null; sessionGeneration: number; instanceId: string };

/** Состояние навигации выхода: адрес без параметра означает выход, а не потерю параметра */
interface ExitState {
	exitTraining?: boolean;
}

export function WorkspaceDataSourceProvider({ children }: { children: ReactNode }) {
	const { t } = useTranslation('common');
	const liveClient = useQueryClient();
	const location = useLocation();
	const navigate = useNavigate();
	const identity = useSessionStore((state) => sessionIdentity(state.user));
	const authGeneration = useSessionStore((state) => state.generation);
	const exiting = (location.state as ExitState | null)?.exitTraining === true;
	const fromUrl = new URLSearchParams(location.search).get(TRAINING_INSTANCE_PARAM);
	const desired =
		identity === null
			? null
			: (fromUrl ??
				(exiting
					? null
					: (trainingInstanceFromRoute(location.pathname, location.search) ??
						readActiveInstance(identity))));
	/* Учебный manifest принадлежит входу, live-форма той же личности переживает reauth. */
	const sessionGeneration = desired === null ? 0 : authGeneration;
	const restoration = useRef<{
		identity: string | null;
		sessionGeneration: number;
		instanceId: string | null;
		pending: ReturnType<typeof resetTrainingInstance> | null;
		complete: boolean;
	} | null>(null);
	const [generation, setGeneration] = useState(0);
	const [active, setActive] = useState<Active>({ kind: 'switching' });
	/* Подключённый источник для перехода: прежний кеш отменяется и очищается при смене */
	const activeRef = useRef(active);
	useEffect(() => {
		activeRef.current = active;
	}, [active]);

	/* Переключение контура: страницы размонтированы, пока маршрутизатор не сменится */
	useEffect(() => {
		/* Полный reload восстанавливает исходные данные примера, сохраняя достижения.
		   Promise в ref общий для повторного эффекта StrictMode; SPA-переходы его не запускают. */
		if (identity !== null && restoration.current === null) {
			const navigation = performance.getEntriesByType?.('navigation')[0] as
				| PerformanceNavigationTiming
				| undefined;
			restoration.current = {
				identity,
				sessionGeneration,
				instanceId: navigation?.type === 'reload' ? desired : null,
				pending: null,
				complete: false,
			};
		}
		if (
			restoration.current !== null &&
			(restoration.current.identity !== identity ||
				restoration.current.sessionGeneration !== sessionGeneration)
		) {
			/* Смена сессии — не reload: новый владелец проверяет доступ своим GET.
			   Старое замыкание может завершить только собственную Promise. */
			restoration.current = {
				identity,
				sessionGeneration,
				instanceId: null,
				pending: null,
				complete: true,
			};
		}
		const previous = activeRef.current;
		if (previous.kind === 'training' || previous.kind === 'live') {
			void previous.source.queryClient.cancelQueries();
		}
		if (previous.kind === 'training') previous.source.queryClient.clear();
		let cancelled = false;
		const ownsRequest = () =>
			!cancelled &&
			(desired === null || useSessionStore.getState().generation === sessionGeneration) &&
			sessionIdentity(useSessionStore.getState().user) === identity;
		if (desired === null) {
			/* Страницы прежнего контура уже размонтированы: рабочий источник подключается
			   отдельным шагом, до монтирования рабочих страниц */
			void Promise.resolve().then(() => {
				if (!ownsRequest()) return;
				const source = liveDataSource(liveClient);
				setRequestRouter(source.router);
				setActive({ kind: 'live', identity, sessionGeneration, source });
			});
			return () => {
				cancelled = true;
			};
		}
		/* Пока manifest не загружен, ни один запрос к данным не уходит */
		setRequestRouter(() => Promise.reject(new Error('training data source is not ready')));
		const restored = restoration.current;
		const needsRestore =
			restored !== null &&
			restored.identity === identity &&
			restored.instanceId === desired &&
			!restored.complete;
		const loading = needsRestore
			? (restored.pending ??= resetTrainingInstance(desired)
					.then((result) => {
						restored.complete = true;
						return result;
					})
					.catch((error: unknown) => {
						restored.pending = null;
						throw error;
					}))
			: getTrainingInstance(desired);
		loading
			.then(({ manifest, version }) => {
				if (!ownsRequest()) return;
				/* Экземпляр пропал во время работы: вкладка его забывает, тур встаёт на паузу */
				const gone = () => {
					if (!ownsRequest()) return;
					clearActiveInstance(identity);
					setActive({ kind: 'unavailable', identity, sessionGeneration, instanceId: desired });
					leaveTour('pause', false);
				};
				const source = trainingDataSource(desired, version, gone);
				setRequestRouter(source.router);
				writeActiveInstance(identity, desired);
				setActive({
					kind: 'training',
					identity,
					sessionGeneration,
					instanceId: desired,
					generation,
					manifest,
					source,
				});
			})
			.catch((error: unknown) => {
				if (!ownsRequest()) return;
				if (error instanceof ApiError && error.status === 404) {
					clearActiveInstance(identity);
					setActive({ kind: 'unavailable', identity, sessionGeneration, instanceId: desired });
				} else {
					setActive({ kind: 'failed', identity, sessionGeneration, instanceId: desired });
				}
			});
		return () => {
			cancelled = true;
		};
	}, [desired, generation, identity, sessionGeneration, liveClient]);

	/* Выход завершён: активный экземпляр вкладки забыт */
	useEffect(() => {
		if (exiting && fromUrl === null) clearActiveInstance(identity);
	}, [exiting, fromUrl, identity]);

	/* Выход в рабочий контур: на обзор или на заданный адрес без параметра экземпляра */
	/* Адрес без параметра экземпляра получает его на месте: страницы не размонтируются */
	const currentSession =
		active.kind !== 'switching' && active.sessionGeneration === sessionGeneration;
	const trainingSource =
		currentSession &&
		active.kind === 'training' &&
		active.identity === identity &&
		active.instanceId === desired
			? active.source
			: null;
	useEffect(() => {
		if (trainingSource === null || fromUrl !== null) return;
		navigate(
			workspaceHref(`${location.pathname}${location.search}${location.hash}`, trainingSource.mode),
			{
				replace: true,
				state: location.state,
			},
		);
	}, [
		fromUrl,
		location.hash,
		location.pathname,
		location.search,
		location.state,
		navigate,
		trainingSource,
	]);

	const exitTraining = useCallback(
		(to?: string) => {
			const target = to ?? `/${withoutTrainingParam(location.search)}`;
			const [pathname, search = ''] = target.split('?');
			navigate(
				{ pathname, search: withoutTrainingParam(search) },
				{ state: { exitTraining: true } satisfies ExitState },
			);
		},
		[location.search, navigate],
	);

	const enterTraining = useCallback(
		(instanceId: string, to = '/') => {
			navigate(workspaceHref(to, { training_instance_id: instanceId }));
		},
		[navigate],
	);

	const resetTraining = useCallback(async () => {
		if (desired === null) return;
		await resetTrainingInstance(desired);
		setGeneration((value) => value + 1);
	}, [desired]);

	const ready =
		currentSession &&
		((desired === null && active.kind === 'live' && active.identity === identity) ||
			(active.kind === 'training' &&
				active.identity === identity &&
				active.instanceId === desired &&
				active.generation === generation));

	const value = useMemo<WorkspaceDataSourceValue>(
		() => ({
			mode:
				currentSession && active.kind === 'training' && active.identity === identity
					? active.source.mode
					: 'live',
			manifest:
				currentSession && active.kind === 'training' && active.identity === identity
					? active.manifest
					: null,
			enterTraining,
			exitTraining,
			resetTraining,
		}),
		[active, identity, currentSession, enterTraining, exitTraining, resetTraining],
	);

	if (
		currentSession &&
		active.kind === 'unavailable' &&
		active.identity === identity &&
		active.instanceId === desired
	) {
		return (
			<WorkspaceDataSourceContext.Provider value={value}>
				<main className="ws-main" id="main">
					<div className="ws-empty-state" role="alert">
						<WorkspaceIcon name="alert" size={28} />
						<h3>{t('training.instanceUnavailable.title')}</h3>
						<p>{t('training.instanceUnavailable.hint')}</p>
						<button type="button" className="ws-btn ws-primary" onClick={() => exitTraining()}>
							{t('training.banner.exit')}
						</button>
					</div>
				</main>
			</WorkspaceDataSourceContext.Provider>
		);
	}
	if (
		currentSession &&
		active.kind === 'failed' &&
		active.identity === identity &&
		active.instanceId === desired
	) {
		return (
			<WorkspaceDataSourceContext.Provider value={value}>
				<WorkspaceState state="error" retry={() => setGeneration((value) => value + 1)} />
			</WorkspaceDataSourceContext.Provider>
		);
	}
	if (!ready || (active.kind !== 'live' && active.kind !== 'training')) {
		return <WorkspaceState state="loading" />;
	}
	return (
		<WorkspaceDataSourceContext.Provider value={value}>
			<QueryClientProvider client={active.source.queryClient}>{children}</QueryClientProvider>
		</WorkspaceDataSourceContext.Provider>
	);
}
